import type { TFormCreateBeneficiar } from "components/BeneficiaryFormContainer/types";
import type { IRepresentativeFormValues } from "components/shared/Representatives/RepresentativeForm";
import type { TFormCreateTitle } from "components/TitleFormContainer/types";
import type { ITermFormValues } from "containers/TermFormContainer";
import type { ITransferTermFormValues } from "containers/TransferTermFormContainer";
import type { Dispatch, SetStateAction } from "react";
import type { FieldErrors } from "react-hook-form";

interface IOpenEditMode {
    elementListRef?: any;
    elementId?: string;
    setEditMode: Dispatch<SetStateAction<boolean>>;
    formStep?: string;
}

/**
 * Opens edit page when using NewForm component (for Beneficiaries and Transferees) and scrolls to the element on side list.
 * @param elementListRef All refs of the list.
 * @param elementId Id of the element being edited.
 * @param setEditMode State function.
 */
export const openEditMode = async ({ elementListRef, elementId, setEditMode, formStep }: IOpenEditMode) => {
    await setEditMode(true);

    // Scrolls to element on the left list:
    if (elementId && elementListRef) {
        const currentElementRef = elementListRef[elementId];
        currentElementRef?.scrollIntoView();
    }

    // Scrolls to step in the edit form (if element passed):
    if (formStep) {
        const targetStep = document.querySelector(formStep);
        targetStep?.scrollIntoView({ behavior: "smooth" });
    }
};

type TFormErrors = FieldErrors<
    TFormCreateTitle | TFormCreateBeneficiar | ITermFormValues | IRepresentativeFormValues | ITransferTermFormValues
>;

/**
 * Handle scroll to errors when submit form on edit:
 */
export const handleScrollToErrors = (errors: TFormErrors) => {
    if (!errors) return;
    let errorFieldName = Object.keys(errors)[0];

    // If first error is an array (when using fieldArray):
    if (Array.isArray(errors[errorFieldName])) {
        // Get the index of the first error within that array:
        const fieldIndex = (errors[errorFieldName] as any).findIndex((err) => !!err).toString();
        // Get the field name of the first error within that array
        const fieldName = Object.keys((errors[errorFieldName] as any)[+fieldIndex])[0];

        // Structure the name of the field with that info. Ex: overriddenChannelsData.0.price
        errorFieldName = `${errorFieldName}.${fieldIndex}.${fieldName}`;
    }

    if (errorFieldName === "customFields") {
        errorFieldName = ((errors as FieldErrors<TFormCreateTitle | TFormCreateBeneficiar>).customFields as any).filter(
            (err) => !!err,
        )[0].value.ref.name;
    }

    if (errorFieldName === "countryCodes") {
        errorFieldName = "filters";
    }

    const fieldEl = document.querySelector(`[name="${errorFieldName}"]`); // useRef would be too complex to implement here (too much code to add)
    if (fieldEl) {
        fieldEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }
};
